import { Wrench, FlaskConical, MessageSquarePlus, Loader2 } from 'lucide-react';
import useAdminAi from '../../hooks/useAdminAi';
import AdminAiChat from './AdminAiChat';
import { BetaReports, ChangeLog, FaqReview, NotesBox } from './AdminAiSections';
import HelpDrafts from './HelpDrafts';
import HelpOverrides from './HelpOverrides';

const HEALTH_LABELS = [
  ['total', 'Items'], ['perfect', 'Perfect records'], ['missing_title', 'Missing title'],
  ['stale_prices', 'Stale prices'], ['incomplete_prices', 'Missing price points'], ['priority_flagged', 'Priority for batches'],
];

export default function AdminAiPanel() {
  const ai = useAdminAi();
  const { health, busy, msg, run } = ai;
  return (
    <div className="space-y-5" data-testid="admin-ai-panel">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2" data-testid="admin-ai-health">
        {HEALTH_LABELS.map(([k, label]) => (
          <div key={k} className="bg-zinc-900 border border-zinc-800 rounded-lg p-3">
            <div className="text-xl font-black text-white" data-testid={`admin-ai-health-${k}`}>{health ? health[k] : '—'}</div>
            <div className="text-[11px] text-zinc-500">{label}</div>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-2">
        <ActionBtn icon={Wrench} label="Run library maintenance" active={busy === 'maint'} testid="admin-ai-run-maintenance-btn"
          onClick={() => run('maint', '/maintenance/run', {}, (d) => `Maintenance done: ${d.title_reports.applied} titles fixed, ${d.ocr_rematch.rematched} screenshot lines matched, ${d.priority.flagged} flagged / ${d.priority.unflagged} unflagged for batches.`)} />
        <ActionBtn icon={FlaskConical} label="Run beta tests" active={busy === 'beta'} testid="admin-ai-run-beta-btn"
          onClick={() => run('beta', '/beta/run', {}, (d) => `Beta report ready: ${d.passed}/${d.total} passed${d.emailed ? ' · emailed to you' : ''}.`)} />
        <ActionBtn icon={MessageSquarePlus} label={`Draft FAQs (${ai.faqs.pending_questions} new questions)`} active={busy === 'faq'} testid="admin-ai-draft-faqs-btn"
          onClick={() => run('faq', '/faqs/draft', {}, (d) => `${d.drafted} FAQ drafts created — review below.`)} />
      </div>
      {msg && <p className="text-sm text-[#FFD700]" data-testid="admin-ai-message">{msg}</p>}
      <HelpDrafts drafts={ai.drafts} reload={ai.reload} setMsg={ai.setMsg} />
      <HelpOverrides overrides={ai.overrides} reload={ai.reload} setMsg={ai.setMsg} />
      <div className="grid lg:grid-cols-2 gap-5">
        <AdminAiChat onToolActivity={ai.reload} />
        <div className="space-y-5">
          <BetaReports reports={ai.reports} />
          <NotesBox notes={ai.notes} run={run} busy={busy} />
        </div>
      </div>
      <FaqReview faqs={ai.faqs.faqs} reload={ai.reload} setMsg={ai.setMsg} />
      <ChangeLog changes={ai.changes} reload={ai.reload} setMsg={ai.setMsg} />
    </div>
  );
}

function ActionBtn({ icon: Icon, label, active, onClick, testid }) {
  return (
    <button onClick={onClick} disabled={active} className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold border border-zinc-700 hover:border-[#00D26A] hover:text-[#00D26A] disabled:opacity-50 transition-colors" data-testid={testid}>
      {active ? <Loader2 className="w-4 h-4 animate-spin" /> : <Icon className="w-4 h-4" />} {label}
    </button>
  );
}
